/**
 * Was in einem gescannten QR-Code stehen kann.
 *
 * **Zwei Formate, eine Kamera.** Der Knopf in der Prüfungsauswahl öffnet denselben Scanner
 * für beides; erst dieser Leser entscheidet, was da kam.
 *
 * ⚠️ **`settings-` ist nicht unsere Erfindung.** So schreibt der Classic-Server seinen Code:
 * `'settings-' + Ext.JSON.encode(source)` (nachgelesen in seinem `js/app.js`, Dialog
 * „Client-Einstellungen"). Der Inhalt ist ein flaches Objekt aus seinen 41 Schlüsseln —
 * darunter `host`, `username` und `password`.
 *
 * ⚠️ **Der Einstellungscode trägt den Serverzugang im Klartext.** Wer ihn abfotografiert, hat
 * Adresse, Anmeldenamen und Passwort. Das ist die Bauart des Fremdsystems; die App sagt es dem
 * Prüfer, bevor sie ihn übernimmt (s. `QrUebernahmeDialog`), und ein solcher Ausdruck gehört
 * nicht an eine Pinnwand.
 *
 * `auswahl-` ist dagegen unser Format — versioniert, damit ein späteres Relay Felder ergänzen
 * kann, ohne dass ältere Geräte etwas halb Verstandenes anwenden.
 *
 * ⭐ **Relays Einstellungscode ist der Hauptweg** (Entscheidung des Nutzers, 2026-10-08): reines
 * JSON ohne Präfix, `{"type":"clientSettings","version":1,"clientType":"TABLET_OSCE",
 * "group":"…","settings":{…}}` (Relay `ClientSettingQrPdf.nutzlast`). `settings` trägt alle
 * Einstellungen der Geräteart unter Relays Namen, alle Werte als Text, Passwörter im Klartext —
 * dieselben wie `SET_SETTINGS`. Ein Code für eine andere Geräteart wird abgewiesen. Der
 * Classic-Code (`settings-`) ist zweitrangig; seine Schlüssel kennt nur
 * `server/classic/einstellungenQr.ts`.
 */

/** Die Fassung, die dieser Leser versteht. Ein Code mit anderer Fassung wird abgewiesen. */
export const AUSWAHL_FASSUNG = 1;

/** Die Fassung von Relays Einstellungscode, die dieser Leser versteht. */
export const RELAY_EINSTELLUNGEN_FASSUNG = 1;

/** Die Geräteart, für die ein Relay-Einstellungscode gedacht sein muss. */
export const GERAETEART = 'TABLET_OSCE';

export type QrNutzlast =
  | { art: 'relayEinstellungen'; gruppe: string | null; werte: Record<string, unknown> }
  | { art: 'einstellungen'; werte: Record<string, unknown> }
  | { art: 'auswahl'; exam: string; station: string | null; examiner: string | null; actor: string | null }
  | { art: 'vorschau'; url: string; id: string }
  | { art: 'unbekannt'; grund: 'fremd' | 'json' | 'fassung' | 'ohneExam' | 'geraeteart' };

const alsObjekt = (roh: string): Record<string, unknown> | null => {
  try {
    const gelesen: unknown = JSON.parse(roh);
    if (typeof gelesen !== 'object' || gelesen === null || Array.isArray(gelesen)) return null;
    return gelesen as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** Eine Kennung aus fremder Hand: nur nichtleere Zeichenketten zählen. */
const kennung = (wert: unknown): string | null =>
  typeof wert === 'string' && wert.trim() !== '' ? wert.trim() : null;

export function lisQrNutzlast(roh: string): QrNutzlast {
  // Scanner hängen gern ein Zeilenende an, und ein führendes Leerzeichen kommt aus manchem
  // Druckerzeugnis mit.
  const text = roh.trim();
  if (text.startsWith('{')) {
    const daten = alsObjekt(text);
    // Relays Preview über Message (`src/preview/`): `{"type":"message","url","id"}`, ohne Fassung
    // und Geräteart — derselbe Code wie für Ink, der Empfänger steckt im Schlüssel.
    if (daten?.type === 'message') {
      const url = kennung(daten.url);
      const id = kennung(daten.id);
      return url && id ? { art: 'vorschau', url, id } : { art: 'unbekannt', grund: 'json' };
    }
    // Nur `clientSettings` ist ein Einstellungscode; jedes andere JSON (ein Ausweis, ein
    // fremdes Etikett) bleibt fremd.
    if (!daten || daten.type !== 'clientSettings') return { art: 'unbekannt', grund: 'fremd' };
    if (daten.version !== RELAY_EINSTELLUNGEN_FASSUNG) return { art: 'unbekannt', grund: 'fassung' };
    if (daten.clientType !== GERAETEART) return { art: 'unbekannt', grund: 'geraeteart' };
    const werte = daten.settings;
    if (typeof werte !== 'object' || werte === null || Array.isArray(werte)) {
      return { art: 'unbekannt', grund: 'json' };
    }
    return {
      art: 'relayEinstellungen',
      gruppe: kennung(daten.group),
      werte: werte as Record<string, unknown>,
    };
  }

  if (text.startsWith('settings-')) {
    const werte = alsObjekt(text.slice('settings-'.length));
    return werte ? { art: 'einstellungen', werte } : { art: 'unbekannt', grund: 'json' };
  }

  if (text.startsWith('auswahl-')) {
    const daten = alsObjekt(text.slice('auswahl-'.length));
    if (!daten) return { art: 'unbekannt', grund: 'json' };
    if (daten.v !== AUSWAHL_FASSUNG) return { art: 'unbekannt', grund: 'fassung' };
    const exam = kennung(daten.exam);
    if (!exam) return { art: 'unbekannt', grund: 'ohneExam' };
    return {
      art: 'auswahl',
      exam,
      station: kennung(daten.station),
      examiner: kennung(daten.examiner),
      actor: kennung(daten.actor),
    };
  }

  return { art: 'unbekannt', grund: 'fremd' };
}

/**
 * Ist das ein Code für das Einrichtungsblatt — Einstellungen (Relay oder Classic) oder Auswahl —,
 * auch einer, den dieser Leser abweist (andere Fassung, andere Geräteart, kaputt)?
 *
 * ⚠️ **Für den Prüflingsscanner.** Der zeigt einen Code, zu dem es keinen Prüfling gibt, im
 * Wortlaut an. Ein Einstellungscode trägt aber Passwörter im Klartext — Relays Code immer das
 * ganze Profil. Er darf dort nur benannt, nie gezeigt werden.
 */
export function istEinrichtungscode(roh: string): boolean {
  const gelesen = lisQrNutzlast(roh);
  return gelesen.art !== 'unbekannt' || gelesen.grund !== 'fremd';
}
